import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { Icons } from '../components/icons';
import { Button, ButtonLink, ErrorNote, Field, Input, Loading, Select, Toggle } from '../components/ui';
import { titleCase } from '../lib/format';
import type { HeardFrom } from '../lib/types';
import { sessionHint } from '../site/hooks';
import { PixelChoices, trackSignUp, usePixelPageVisit } from '../site/pixel';

const SITE_LINKS = [
  ['Home', '/'],
  ['Pricing', '/pricing'],
  ['Terms', '/terms'],
  ['Privacy', '/privacy'],
  ['Refunds', '/refunds'],
  ['Contact', '/contact'],
] as const;

/** Under every button that creates an account: what the person agrees to by pressing it. */
function Agreement({ action }: { action: string }) {
  const link = (to: string, label: string) => (
    <Link to={to} target="_blank" className="text-brand hover:underline">
      {label}
    </Link>
  );
  return (
    <p className="text-center text-[12px] leading-relaxed text-muted">
      By pressing {action}, you agree to the {link('/terms', 'terms of service')} and {link('/dpa', 'data processing terms')} for your business, and confirm you have read the{' '}
      {link('/privacy', 'privacy policy')}.
    </p>
  );
}

/**
 * Where to go after signing in or up: a page that sent the person here (such as /connect, an app asking to connect) puts
 * it in the navigation state. Only paths in this app.
 */
export const nextPath = (state: unknown) => {
  const next = (state as { next?: unknown } | null)?.next;
  return typeof next === 'string' && next.startsWith('/') && !next.startsWith('//') && !next.includes('\\') ? next : null;
};

export function AuthFrame({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-[minmax(0,1fr)_minmax(480px,560px)]">
      <div className="relative hidden overflow-hidden bg-sidebar p-12 text-[#d6e8df] lg:flex lg:flex-col">
        <Link to="/" className="flex w-fit items-center gap-2.5 rounded-md">
          <Icons.Logo size={28} />
          <span className="text-[17px] font-semibold text-white">RareMargin</span>
        </Link>
        <div className="mt-auto max-w-lg">
          <h2 className="text-[30px] leading-tight font-semibold tracking-[-0.02em] text-white">The books close themselves. You approve what matters.</h2>
          <p className="mt-4 text-[14.5px] leading-relaxed text-[#a9c4b8]">
            An AI-native general ledger with continuous close, explainable bank matching, a tamper-evident hash chain and controls that run all the time.
          </p>
          <ul className="mt-8 space-y-3 text-[13.5px]">
            {[
              'Revenue, amortization and accruals post as dates arrive',
              'Every AI or API change waits for a person to approve it',
              'Trial balance, sub-ledger tie-outs and the chain are checked continuously',
            ].map((t) => (
              <li key={t} className="flex gap-2.5">
                <span className="mt-0.5 text-[#7ee0b0]">
                  <Icons.Check />
                </span>
                {t}
              </li>
            ))}
          </ul>
        </div>
        <svg className="pointer-events-none absolute -top-24 -right-24 opacity-[0.07]" width="520" height="520" viewBox="0 0 32 32" aria-hidden>
          <path d="M6.5 16c0-3 2.2-5 4.7-5 4.6 0 4 10 8.6 10 2.5 0 4.7-2 4.7-5s-2.2-5-4.7-5c-4.6 0-4 10-8.6 10-2.5 0-4.7-2-4.7-5z" fill="none" stroke="#fff" strokeWidth="1.2" />
        </svg>
      </div>
      <div className="flex items-center justify-center bg-surface p-8">
        <div className="w-full max-w-sm">
          <Link to="/" className="mb-8 flex w-fit items-center gap-2.5 rounded-md lg:hidden">
            <Icons.Logo size={26} />
            <span className="text-[16px] font-semibold">RareMargin</span>
          </Link>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">{title}</h1>
          <p className="mt-1 text-[13px] text-muted">{subtitle}</p>
          <div className="mt-6">{children}</div>
          <nav aria-label="Site" className="mt-10 flex flex-wrap gap-x-4 gap-y-1.5 border-t border-line pt-5 text-[12.5px] text-muted">
            {SITE_LINKS.map(([label, to]) => (
              <Link key={to} to={to} className="hover:text-ink">
                {label}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
}

/** The sign-in password field, with the reset link after it in tab order but shown beside the label. */
function CurrentPassword({ email, value, onChange, autoFocus }: { email: string; value: string; onChange: (v: string) => void; autoFocus?: boolean }) {
  return (
    <div className="relative">
      <Field label="Password">
        <Input type="password" autoComplete="current-password" required autoFocus={autoFocus} value={value} onChange={(e) => onChange(e.target.value)} />
      </Field>
      <Link to="/forgot-password" state={{ email }} className="absolute top-0 right-0 text-[12px] leading-[18px] text-brand hover:underline">
        Forgot password?
      </Link>
    </div>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  // With two-factor on, a correct password gives a ticket for the code step instead of a session.
  const [ticket, setTicket] = useState<string | null>(null);
  const notice = (location.state as { notice?: string } | null)?.notice;
  const next = nextPath(location.state);

  function signedIn() {
    qc.clear();
    sessionHint.set(true);
    navigate(next ?? '/', { replace: true });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ twoFactorRequired?: boolean; ticket?: string }>('/api/auth/login', { body: { email, password } });
      if (r.twoFactorRequired) setTicket(r.ticket!);
      else signedIn();
    } catch (err) {
      setError(err instanceof ApiError && err.status === 401 ? new Error('That email and password do not match.') : err);
    } finally {
      setBusy(false);
    }
  }

  if (ticket) {
    return (
      <TwoFactorStep
        ticket={ticket}
        onDone={signedIn}
        onRestart={(why) => {
          setTicket(null);
          setPassword('');
          setError(why);
        }}
      />
    );
  }
  return (
    <AuthFrame title="Sign in" subtitle="Welcome back. Your books have been closing while you were away.">
      {notice && <div className="mb-4 rounded-lg border border-info/15 bg-info-soft px-3 py-2 text-[13px] text-info">{notice}</div>}
      <form onSubmit={submit} className="space-y-4">
        <Field label="Work email">
          <Input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
        </Field>
        <CurrentPassword email={email} value={password} onChange={setPassword} />
        <ErrorNote error={error} />
        <Button type="submit" variant="primary" className="w-full" loading={busy}>
          Sign in
        </Button>
      </form>
      {/* Seed instructions are for local development; visitors to the live site never see them. */}
      {import.meta.env.DEV && (
        <div className="mt-6 rounded-lg border border-line bg-paper p-3 text-[12.5px] text-muted">
          <div className="font-medium text-ink-2">Demo company</div>
          Run <code className="rounded bg-sunken px-1 font-mono text-[11.5px]">npm run seed</code> and sign in as <span className="text-ink-2">owner@acme.test</span>, <span className="text-ink-2">controller@acme.test</span> or{' '}
          <span className="text-ink-2">accountant@acme.test</span>. The password is in <code className="font-mono text-[11.5px]">packages/core/src/seed/sample-company.ts</code>.
        </div>
      )}
      <p className="mt-6 text-center text-[13px] text-muted">
        New to RareMargin?{' '}
        <Link to="/signup" state={next ? { next } : undefined} className="font-medium whitespace-nowrap text-brand hover:underline">
          Create a company
        </Link>
      </p>
    </AuthFrame>
  );
}

/** The second step of signing in: a code from the authenticator app, or a recovery code. */
function TwoFactorStep({ ticket, onDone, onRestart }: { ticket: string; onDone: () => void; onRestart: (why: Error) => void }) {
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/login/2fa', { body: { ticket, code } });
      onDone();
    } catch (err) {
      // The ticket lasts 5 minutes; after that, back to the password.
      if (err instanceof ApiError && err.code === 'login_expired') return onRestart(new Error(err.message));
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame title="Two-factor sign-in" subtitle={recovery ? 'Enter one of the recovery codes you saved. Each works once.' : 'Enter the 6-digit code from your authenticator app.'}>
      <form onSubmit={submit} className="space-y-4">
        <Field label={recovery ? 'Recovery code' : 'Code'}>
          <Input
            key={String(recovery)}
            autoFocus
            required
            autoComplete="one-time-code"
            inputMode={recovery ? 'text' : 'numeric'}
            placeholder={recovery ? 'xxxxx-xxxxx' : '123456'}
            maxLength={recovery ? 20 : 7}
            className="font-mono tracking-[0.2em]"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </Field>
        <ErrorNote error={error} />
        <Button type="submit" variant="primary" className="w-full" loading={busy}>
          Sign in
        </Button>
      </form>
      <p className="mt-6 text-center text-[13px] text-muted">
        <button type="button" className="font-medium text-brand hover:underline" onClick={() => (setRecovery(!recovery), setCode(''), setError(null))}>
          {recovery ? 'Use a code from the app' : 'Lost your phone? Use a recovery code'}
        </button>
      </p>
    </AuthFrame>
  );
}

const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD', 'SGD', 'JPY'];

/** Answers to "How did you hear about RareMargin?", in the order shown, and the follow-up each asks. None for a colleague, to keep names out. */
export const HEARD_FROM: Record<HeardFrom, { label: string; ask?: string }> = {
  search: { label: 'Google or another search engine', ask: 'What did you search for?' },
  chatgpt: { label: 'ChatGPT', ask: 'What did you ask it?' },
  claude: { label: 'Claude', ask: 'What did you ask it?' },
  perplexity: { label: 'Perplexity', ask: 'What did you ask it?' },
  gemini: { label: 'Gemini or Google’s AI answers', ask: 'What did you ask it?' },
  other_ai: { label: 'Another AI assistant', ask: 'Which one, and what did you ask it?' },
  reddit: { label: 'Reddit', ask: 'Which subreddit or post?' },
  linkedin: { label: 'LinkedIn', ask: 'Which post?' },
  x: { label: 'X (Twitter)', ask: 'Which post?' },
  youtube: { label: 'YouTube', ask: 'Which video or channel?' },
  colleague: { label: 'A colleague or friend' },
  other: { label: 'Somewhere else', ask: 'Where?' },
};

export function SignupPage() {
  usePixelPageVisit();
  const navigate = useNavigate();
  const location = useLocation();
  const next = nextPath(location.state);
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    companyName: '',
    baseCurrency: 'USD',
    sampleData: true,
    heardFrom: '' as HeardFrom | '',
    heardFromDetail: '',
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const fieldErr = (p: string) => (error instanceof ApiError ? error.fieldErrors.find((e) => e.path === p)?.message : undefined);
  const ask = form.heardFrom ? HEARD_FROM[form.heardFrom].ask : undefined;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Both answers are optional: an unanswered question, or a detail its answer doesn't ask for, isn't sent.
      const heard = { heardFrom: form.heardFrom || undefined, heardFromDetail: (ask && form.heardFromDetail.trim()) || undefined };
      await api('/api/auth/signup', { body: { ...form, ...heard } });
      await trackSignUp();
      qc.clear();
      sessionHint.set(true);
      navigate(next ?? '/', { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame title="Create your company" subtitle="Start with a sandbox full of realistic data, or an empty ledger.">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Your name" error={fieldErr('name')}>
          <Input required value={form.name} onChange={(e) => set('name', e.target.value)} autoComplete="name" />
        </Field>
        <Field label="Work email" error={fieldErr('email')}>
          <Input type="email" required value={form.email} onChange={(e) => set('email', e.target.value)} autoComplete="email" />
        </Field>
        <Field label="Password" hint="At least 10 characters" error={fieldErr('password')}>
          <Input type="password" required minLength={10} value={form.password} onChange={(e) => set('password', e.target.value)} autoComplete="new-password" />
        </Field>
        <div className="grid grid-cols-[minmax(0,1fr)_110px] gap-3">
          <Field label="Company" error={fieldErr('companyName')}>
            <Input required value={form.companyName} onChange={(e) => set('companyName', e.target.value)} />
          </Field>
          <Field label="Currency">
            <Select value={form.baseCurrency} onChange={(e) => set('baseCurrency', e.target.value)} disabled={form.sampleData}>
              {CURRENCIES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="rounded-lg border border-line p-3">
          <Toggle checked={form.sampleData} onChange={(v) => set('sampleData', v)} label="Load a sample company" description="14 months of SaaS history: contracts, bills, bank feeds, two entities (US and UK)." />
        </div>
        <Field label="How did you hear about RareMargin?" hint="Optional. It tells us where people find us." error={fieldErr('heardFrom')}>
          <Select value={form.heardFrom} onChange={(e) => set('heardFrom', e.target.value as HeardFrom | '')}>
            <option value="">Choose one</option>
            {(Object.keys(HEARD_FROM) as HeardFrom[]).map((k) => (
              <option key={k} value={k}>
                {HEARD_FROM[k].label}
              </option>
            ))}
          </Select>
        </Field>
        {ask && (
          <Field label={`${ask} (optional)`} error={fieldErr('heardFromDetail')}>
            <Input value={form.heardFromDetail} maxLength={200} onChange={(e) => set('heardFromDetail', e.target.value)} />
          </Field>
        )}
        <ErrorNote error={error instanceof ApiError && error.fieldErrors.length ? null : error} />
        <Button type="submit" variant="primary" className="w-full" loading={busy}>
          {busy ? 'Creating your company…' : 'Create company'}
        </Button>
        <Agreement action="Create company" />
      </form>
      <PixelChoices />
      <p className="mt-6 text-center text-[13px] text-muted">
        Already have an account?{' '}
        <Link to="/login" state={next ? { next } : undefined} className="font-medium whitespace-nowrap text-brand hover:underline">
          Sign in
        </Link>
      </p>
    </AuthFrame>
  );
}

// --- Password reset and invites. Links carry the token in the URL fragment, so it never reaches a server log. ------

/** The token from `#token=…`, read once; the fragment is then dropped from the address bar and the history entry. */
function useLinkToken(): string {
  const location = useLocation();
  const navigate = useNavigate();
  const [token] = useState(() => new URLSearchParams(location.hash.slice(1)).get('token') ?? '');
  useEffect(() => {
    if (location.hash) navigate({ pathname: location.pathname, search: location.search }, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate]);
  return token;
}

function BackToSignIn() {
  return (
    <p className="mt-6 text-center text-[13px] text-muted">
      <Link to="/login" className="font-medium text-brand hover:underline">
        Back to sign in
      </Link>
    </p>
  );
}

function Expired({ children }: { children: React.ReactNode }) {
  return <div className="rounded-lg border border-line bg-paper p-3 text-[13px] text-ink-2">{children}</div>;
}

export function ForgotPasswordPage() {
  const location = useLocation();
  const [email, setEmail] = useState(() => (location.state as { email?: string } | null)?.email ?? '');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/forgot', { body: { email } });
      setSent(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthFrame title="Check your email" subtitle={`If ${email} has a RareMargin account, a link to choose a new password is on its way.`}>
        <p className="text-[13px] text-muted">The link works once and expires in an hour. Nothing there? Check spam, or try again in a few minutes.</p>
        <Button className="mt-5 w-full" onClick={() => setSent(false)}>
          Use a different email
        </Button>
        <BackToSignIn />
      </AuthFrame>
    );
  }
  return (
    <AuthFrame title="Reset your password" subtitle="Enter the email you sign in with and we'll send you a link.">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Work email">
          <Input type="email" autoComplete="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
        </Field>
        <ErrorNote error={error} />
        <Button type="submit" variant="primary" className="w-full" loading={busy}>
          Send reset link
        </Button>
      </form>
      <BackToSignIn />
    </AuthFrame>
  );
}

export function ResetPasswordPage() {
  const token = useLinkToken();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const expired = error instanceof ApiError && error.code === 'link_expired';

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) return setError(new Error('The two passwords do not match.'));
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ ok: boolean; signedIn: boolean }>('/api/auth/reset', { body: { token, password } });
      qc.clear();
      sessionHint.set(r.signedIn);
      // With two-factor on, the email link alone doesn't sign in: the new password and a code do.
      navigate(r.signedIn ? '/' : '/login', { replace: true, state: r.signedIn ? undefined : { notice: 'Your password is changed. Sign in with it to continue.' } });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (!token || expired) {
    return (
      <AuthFrame title="This link no longer works" subtitle="Reset links work once and expire after an hour.">
        <Expired>
          Ask for a new one and use the most recent email.{' '}
          <Link to="/forgot-password" className="font-medium text-brand hover:underline">
            Send a new link
          </Link>
        </Expired>
        <BackToSignIn />
      </AuthFrame>
    );
  }
  return (
    <AuthFrame title="Choose a new password" subtitle="You'll be signed out everywhere else, then signed in here.">
      <form onSubmit={submit} className="space-y-4">
        <Field label="New password" hint="At least 10 characters">
          <Input type="password" autoComplete="new-password" required minLength={10} autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Field label="Confirm new password">
          <Input type="password" autoComplete="new-password" required minLength={10} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        <ErrorNote error={error} />
        <Button type="submit" variant="primary" className="w-full" loading={busy}>
          Set password and sign in
        </Button>
      </form>
    </AuthFrame>
  );
}

/** Opened from the confirmation email. The link itself is the proof, so it confirms on load with nothing to fill in. */
export function VerifyEmailPage() {
  const token = useLinkToken();
  const qc = useQueryClient();
  const [result, setResult] = useState<{ email: string } | null>(null);
  const [error, setError] = useState<unknown>(null);
  // The link works once, and StrictMode runs effects twice in development.
  const sent = useRef(false);
  useEffect(() => {
    if (!token || sent.current) return;
    sent.current = true;
    api<{ ok: boolean; email: string }>('/api/auth/verify-email', { body: { token } }).then(
      (r) => {
        setResult(r);
        void qc.invalidateQueries({ queryKey: ['/api/auth/me'] });
      },
      (err) => setError(err),
    );
  }, [token, qc]);
  const next = sessionHint.get() ? '/' : '/login';

  if (!token || (error instanceof ApiError && error.code === 'link_expired')) {
    return (
      <AuthFrame title="This link no longer works" subtitle="Confirmation links work once and expire after 3 days.">
        <Expired>Sign in and choose "Send a new link" in the banner at the top of the app. Use the most recent email.</Expired>
        <p className="mt-6 text-center text-[13px]">
          <Link to={next} className="font-medium text-brand hover:underline">
            Open RareMargin
          </Link>
        </p>
      </AuthFrame>
    );
  }
  if (error) {
    return (
      <AuthFrame title="We couldn't confirm your email" subtitle="Try the link again in a moment.">
        <ErrorNote error={error} />
        <BackToSignIn />
      </AuthFrame>
    );
  }
  if (!result) {
    return (
      <AuthFrame title="Confirming your email" subtitle="This takes a moment.">
        <Loading />
      </AuthFrame>
    );
  }
  return (
    <AuthFrame title="Your email is confirmed" subtitle={`${result.email} is yours. The assistant and invites are now on.`}>
      <ButtonLink to={next} variant="primary" className="w-full">
        Open RareMargin
      </ButtonLink>
    </AuthFrame>
  );
}

interface InviteDetails {
  orgName: string;
  email: string;
  role: string;
  invitedBy: string | null;
  hasAccount: boolean;
  expiresAt: string;
}

export function AcceptInvitePage() {
  const token = useLinkToken();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const invite = useQuery<InviteDetails, ApiError>({
    queryKey: ['invite', token],
    queryFn: () => api<InviteDetails>('/api/auth/invite/lookup', { body: { token } }),
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ signedIn: boolean }>('/api/auth/invite/accept', { body: { token, password, ...(invite.data?.hasAccount ? {} : { name }) } });
      qc.clear();
      sessionHint.set(r.signedIn);
      // An account with two-factor on has joined, and signs in with its code as usual.
      if (r.signedIn) navigate('/', { replace: true });
      else navigate('/login', { replace: true, state: { notice: `You have joined ${invite.data?.orgName ?? 'the company'}. Sign in to open it.` } });
    } catch (err) {
      setError(err instanceof ApiError && err.status === 401 ? new Error('That password is not right for this account.') : err);
    } finally {
      setBusy(false);
    }
  }

  const gone = !token || (error instanceof ApiError && error.code === 'link_expired') || (invite.error && invite.error.code === 'link_expired');
  if (gone) {
    return (
      <AuthFrame title="This invite no longer works" subtitle="Invites expire after 7 days, and each one can be used once.">
        <Expired>Ask the person who invited you to send a new invite.</Expired>
        <BackToSignIn />
      </AuthFrame>
    );
  }
  if (invite.error) {
    return (
      <AuthFrame title="Join a company" subtitle="We couldn't open this invite.">
        <ErrorNote error={invite.error} />
        <BackToSignIn />
      </AuthFrame>
    );
  }
  if (!invite.data) {
    return (
      <AuthFrame title="Join a company" subtitle="Opening your invite…">
        <Loading />
      </AuthFrame>
    );
  }

  const d = invite.data;
  const subtitle = `${d.invitedBy ? `${d.invitedBy} invited you` : "You're invited"} to join as ${titleCase(d.role).toLowerCase()}.`;
  return (
    <AuthFrame title={`Join ${d.orgName}`} subtitle={subtitle}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email">
          <Input type="email" value={d.email} readOnly disabled autoComplete="username" />
        </Field>
        {d.hasAccount ? (
          <>
            <p className="text-[12.5px] text-muted">You already have a RareMargin account. Enter its password to add {d.orgName} to it.</p>
            <CurrentPassword email={d.email} value={password} onChange={setPassword} autoFocus />
          </>
        ) : (
          <>
            <Field label="Your name">
              <Input required autoFocus autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Choose a password" hint="At least 10 characters">
              <Input type="password" autoComplete="new-password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
          </>
        )}
        <ErrorNote error={error} />
        <Button type="submit" variant="primary" className="w-full" loading={busy}>
          {d.hasAccount ? 'Sign in and join' : 'Create account and join'}
        </Button>
        <Agreement action={d.hasAccount ? 'Sign in and join' : 'Create account and join'} />
      </form>
    </AuthFrame>
  );
}
